import { Capacitor } from '@capacitor/core';

export const generateRandomString = (length: number) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/** Exchange Google authorization code for id_token via backend (web flow only; native uses exchange-native-token). */
const exchangeGoogleCode = async (
  code: string,
  redirectUri: string
): Promise<{ id_token: string }> => {
  const response = await fetch('/api/google/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      redirect_uri: redirectUri,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to exchange authorization code');
  }

  if (!data.id_token) {
    throw new Error('No id_token received from Google token exchange');
  }

  return { id_token: data.id_token };
};

function handleAuthUrlError(response: Response, err: { error?: string }): never {
  const msg = err.error || `Failed to get auth URL: ${response.status}`;
  if (msg.includes('GOOGLE_CLIENT_ID') || msg.includes('GOOGLE_CLIENT_SECRET')) {
    throw new Error('Sign-in is not configured. The server is missing GOOGLE_CLIENT_ID or GOOGLE_CLIENT_SECRET in server/.env. See server/.env.example.');
  }
  throw new Error(msg);
}

/** Redirect to Google Sign-In (Gmail only). Web: backend auth URL then redirect. Native: navigate to auth-start-native; native app intercepts and opens in ASWebAuthenticationSession, then loads exchange URL in web view. */
export const redirectToAuth = async () => {
  const isNative = Capacitor.isNativePlatform();
  const redirectUri = `${window.location.origin}/login/callback`;

  if (isNative) {
    // Native: navigate to backend auth-start-native. The native app (iOS/Android) intercepts this
    // and accounts.google.com, opens in ASWebAuthenticationSession; when server redirects to
    // healthscores://login?sso_token=..., the app loads /callback?token=... which redirects to /login/callback#bearer_token=...
    // in the web view, which sets bearer_token and redirects to /dashboard.
    window.location.href = `${window.location.origin}/api/google/auth-start-native`;
    return;
  }

  // Web: get auth URL from backend and redirect
  const response = await fetch(`/api/google/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    handleAuthUrlError(response, err);
  }
  const { auth_url, state } = await response.json();
  sessionStorage.setItem('google_login_state', state);
  sessionStorage.setItem('google_login_redirect_uri', redirectUri);
  window.location.href = auth_url;
};

/** Called from Callback page after redirect (web only): exchange code for id_token and store. */
export const finishGoogleLogin = async (
  code: string,
  redirectUri: string
): Promise<void> => {
  const { id_token } = await exchangeGoogleCode(code, redirectUri);
  localStorage.setItem('bearer_token', id_token);
  sessionStorage.removeItem('google_login_state');
  sessionStorage.removeItem('google_login_redirect_uri');
};

export const checkAuthToken = () => {
  const token = localStorage.getItem('bearer_token');
  return !!token;
};

export const requireAuth = () => {
  if (!checkAuthToken()) {
    window.location.href = '/login';
    return false;
  }
  return true;
};
